import React, {createContext, useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import roleService from "../../../../../services/web/role-service.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";

export const RoleContext = createContext(undefined);

const RoleProvider = ({children}) => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const copy = searchParams.get("cp");
    const roleId = id ?? copy;

    const [name, setName] = useState("")
    const [permissionsSelected, setPermissionsSelected] = useState([]);
    const [lanesSelected, setLanesSelected] = useState([]);
    const [maintenanceSelected, setMaintenanceSelected] = useState(null);
    const [permissionsWithDependenciesNotChecked, setPermissionsWithDependenciesNotChecked] = useState([]);
    const [protectionProgramSelected, setProtectionProgramSelected] = useState([]);

    const [activeUsers, setActiveUsers] = useState([]);
    const [inativeUsers, setInativeUsers] = useState([]);

    const lanesRef = useRef([]);
    const maintenancesRef = useRef(undefined);
    const protectionProgramRef = useRef([]);

    const {data, isLoading, isSuccess, refetch} = useQuery({
        queryFn: () => roleService.get(roleId),
        queryKey: ["role", roleId],
        enabled: !!roleId
    });

    useEffect(() => {
        setName("");
        setPermissionsSelected([]);
        setLanesSelected([]);
        setMaintenanceSelected(null);
        setPermissionsWithDependenciesNotChecked([]);
        setProtectionProgramSelected([]);
        setActiveUsers([]);
        setInativeUsers([]);
        lanesRef.current = [];
        maintenancesRef.current = undefined;
        protectionProgramRef.current = [];
    }, [roleId]);

    useEffect(() => {
        if (!isSuccess) return;
        const {permissions, lanes, maintenanceGroup, name, activeUsers, inativeUsers, programs} = data;

        const isCapturePlaceNocSelected = permissions.some(permission => {
            return permission.name === PermissionEnum.CAPTURE_PLACE_NOC;
        });
        const selectedMaintenanceGroup = maintenanceGroup ?? (isCapturePlaceNocSelected ? {value: 'all', label: 'Todos'} : null);

        maintenancesRef.current = selectedMaintenanceGroup;
        setMaintenanceSelected(selectedMaintenanceGroup);

        const lanesId = lanes?.map(l => parseInt(l));
        lanesRef.current = lanesId;

        const programsId = programs.map(l => parseInt(l));
        protectionProgramRef.current = programsId;

        setName(name);
        setLanesSelected(lanesId);
        setPermissionsSelected(permissions);
        setProtectionProgramSelected(programsId);

        setActiveUsers(activeUsers);
        setInativeUsers(inativeUsers);
    }, [data, isSuccess]);

    const value = useMemo(
        () => ({
            name, setName,
            permissionsSelected, setPermissionsSelected,
            lanesSelected, setLanesSelected,
            maintenanceSelected, setMaintenanceSelected,
            isLoadingFetch: isLoading,
            permissionsWithDependenciesNotChecked,
            setPermissionsWithDependenciesNotChecked,
            maintenancesRef,
            lanesRef,
            activeUsers, setActiveUsers,
            inativeUsers, setInativeUsers,
            refetch,
            fetchData: data,
            protectionProgramSelected, setProtectionProgramSelected, protectionProgramRef
        }),
        [roleId, name, data, permissionsSelected, permissionsWithDependenciesNotChecked, maintenanceSelected, lanesSelected, protectionProgramSelected, activeUsers, inativeUsers, isLoading, refetch]
    );

    return (
        <RoleContext.Provider value={value}>
            {children}
        </RoleContext.Provider>
    );
};

export default RoleProvider;